import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Badge,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Task as TaskIcon,
  Schedule,
  CheckCircle,
  PlayArrow,
  Cancel,
  Visibility,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

export default function MyTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Step 2: State for currently expanded column (only one at a time)
  const [expandedColumn, setExpandedColumn] = useState('Assigned'); // Assigned expanded by default

  useEffect(() => {
    const fetchMyTasks = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('https://propertyearn.net/api/api/my-tasks', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to fetch tasks');
        
        const data = await response.json();
        setTasks(data.tasks || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMyTasks();
  }, []);

  // Step 3: Toggle function for expanding/collapsing columns (accordion behavior)
  const toggleColumn = (status) => {
    // If clicking the already expanded column, collapse it
    // If clicking a different column, expand it and collapse others
    setExpandedColumn(expandedColumn === status ? null : status);
  };

  // Step 4: Check if a column is currently expanded
  const isColumnExpanded = (status) => {
    return expandedColumn === status;
  };

  // Step 5: Group tasks by four status types
  const groupTasksByStatus = () => {
    const grouped = {
      'Assigned': [],
      'Pending Review': [],
      'Completed': [],
      'Rejected': []
    };

    tasks.forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      } else {
        grouped['Assigned'].push(task);
      }
    });

    return grouped;
  };

  // Step 6: Get status color for chips
  const getStatusColor = (status) => {
    switch (status) {
      case 'Assigned': return 'primary';
      case 'In Progress': return 'warning';
      case 'Pending Review': return 'info';
      case 'Completed': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  // Step 7: Get appropriate icon for each status column
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Assigned': return <TaskIcon color="primary" />;
      case 'Pending Review': return <Visibility color="info" />;
      case 'Completed': return <CheckCircle color="success" />;
      case 'Rejected': return <Cancel color="error" />;
      default: return <TaskIcon color="primary" />;
    }
  };

  // Step 8: Get badge color for each status column
  const getBadgeColor = (status) => {
    switch (status) {
      case 'Assigned': return 'primary';
      case 'Pending Review': return 'info';
      case 'Completed': return 'success';
      case 'Rejected': return 'error';
      default: return 'primary';
    }
  };

  // Step 9: Create reusable Task Column Component with Accordion behavior
  const TaskColumn = ({ status, tasks, icon, isExpanded, onToggle }) => (
    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Card 
        elevation={isExpanded ? 3 : 1} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          border: isExpanded ? '2px solid' : '1px solid',
          borderColor: isExpanded ? 'primary.main' : 'divider',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            borderColor: isExpanded ? 'primary.main' : 'text.secondary',
            elevation: isExpanded ? 3 : 2
          }
        }}
        onClick={() => onToggle(status)}
      >
        {/* Column Header with Toggle Button */}
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Badge 
                  badgeContent={tasks.length} 
                  color={getBadgeColor(status)}
                  sx={{ 
                    '& .MuiBadge-badge': { 
                      fontSize: '0.7rem', 
                      height: '20px', 
                      minWidth: '20px',
                    } 
                  }}
                >
                  <Typography variant="h6" component="h2">
                    {status}
                  </Typography>
                </Badge>
              </Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card click event
                  onToggle(status);
                }}
                size="small"
                sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          }
          sx={{ 
            borderBottom: isExpanded ? '2px solid' : 'none',
            borderColor: 'primary.main',
            bgcolor: isExpanded ? 'action.selected' : 'background.default',
            py: 1,
            '& .MuiCardHeader-content': { width: '100%' }
          }}
        />
        
        {/* Collapsible Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ p: 0, flexGrow: 1, '&:last-child': { pb: 0 } }}>
            {tasks.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary={`No ${status.toLowerCase()} tasks`} 
                  sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}
                />
              </ListItem>
            ) : (
              <List sx={{ maxHeight: '500px', overflow: 'auto' }}>
                {tasks.map((task) => (
                  <ListItem 
                    key={task.id} 
                    divider 
                    sx={{ 
                      flexDirection: 'column', 
                      alignItems: 'flex-start',
                      py: 2,
                      px: 2
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent collapse when clicking on task
                  >
                    {/* Task Header with Icon and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        <TaskIcon 
                          color={
                            status === 'Completed' ? 'success' :
                            status === 'Rejected' ? 'error' :
                            status === 'Pending Review' ? 'info' : 'primary'
                          } 
                        />
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" fontWeight="medium">
                            {task.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            {/* Task Description */}
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                              {task.description?.substring(0, 80)}...
                            </Typography>
                            
                            {/* Task Metadata Chips */}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {/* Status Chip */}
                              <Chip
                                label={task.status}
                                color={getStatusColor(task.status)}
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              
                              {/* Reward Amount Chip */}
                              <Chip
                                icon={<Schedule sx={{ fontSize: '0.9rem' }} />}
                                label={`£${task.reward_amount}`}
                                variant="outlined"
                                size="small"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              
                              {/* Deadline Chip (if exists) */}
                              {task.deadline && (
                                <Chip
                                  label={`Due: ${new Date(task.deadline).toLocaleDateString()}`}
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </Box>
                    
                  {/* Action Button - Only show for Assigned tasks (remove Pending Review) */}
                    {status === 'Assigned' && (
                      <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent collapse when clicking button
                          navigate(`/task/${task.id}/execute`);
                        }}
                        sx={{ mt: 1, alignSelf: 'flex-end' }}
                        size="small"
                      >
                        Continue
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Collapse>

        {/* Collapsed State Indicator */}
        {!isExpanded && tasks.length > 0 && (
          <Box 
            sx={{ 
              p: 1, 
              textAlign: 'center', 
              bgcolor: 'action.hover',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
            onClick={(e) => e.stopPropagation()} // Allow click through to card
          >
            <Typography variant="caption" color="text.secondary">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''} - Click to expand
            </Typography>
          </Box>
        )}
      </Card>
    </Grid>
  );

  // Step 10: Loading State
  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  // Step 11: Get grouped tasks
  const groupedTasks = groupTasksByStatus();

  return (
    <DashboardLayout>
      {/* Page Header */}
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        My Tasks
      </Typography>

      {/* Instructions */}
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Click on any column to expand it. Only one column can be expanded at a time.
        {expandedColumn && ` Currently viewing: ${expandedColumn}`}
      </Typography>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Step 12: Four-column layout with accordion behavior */}
      <Grid container spacing={2}>
        {/* Assigned Tasks Column - Expanded by default */}
        <TaskColumn 
          status="Assigned" 
          tasks={groupedTasks['Assigned']} 
          icon={getStatusIcon('Assigned')}
          isExpanded={isColumnExpanded('Assigned')}
          onToggle={toggleColumn}
        />
        
        {/* Pending Review Tasks Column */}
        <TaskColumn 
          status="Pending Review" 
          tasks={groupedTasks['Pending Review']} 
          icon={getStatusIcon('Pending Review')}
          isExpanded={isColumnExpanded('Pending Review')}
          onToggle={toggleColumn}
        />
        
        {/* Completed Tasks Column */}
        <TaskColumn 
          status="Completed" 
          tasks={groupedTasks['Completed']} 
          icon={getStatusIcon('Completed')}
          isExpanded={isColumnExpanded('Completed')}
          onToggle={toggleColumn}
        />
        
        {/* Rejected Tasks Column */}
        <TaskColumn 
          status="Rejected" 
          tasks={groupedTasks['Rejected']} 
          icon={getStatusIcon('Rejected')}
          isExpanded={isColumnExpanded('Rejected')}
          onToggle={toggleColumn}
        />
      </Grid>
    </DashboardLayout>
  );
}